import { MouseEvent, useMemo } from 'react';
import { Avatar } from '@intigral/shared';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { getPathNameWithoutLangPrefix } from '@intigral/utils';
import type { BottomTabItem } from '@intigral/ui';
import { IStore } from '../interfaces/StoreTypes';
import url from '../helpers/url';
import useLanguage from './useLanguage';
import useProfileAvatars from './useProfileAvatars';
import { getProfileAvatar } from '../features/header/utils/headerUtils';
import { openSearchInputOverlay } from '../store/reducers/headerSlice';
import { getGuestOpenScreensFromMenu, isGuestAllowedPrimarySegment, isGuestUser } from '../guest/guards';
import { showGuestPromotionPopup } from '../guest/redirects';
import { getUserInfo } from '../utils/user';
import { useHeaderMenuQuery } from './useHeaderMenuQuery';

// TODO: replace with translation keys once they are added to the CMS / fallback files
const LABELS = {
    en: { home: 'Home', livetv: 'Live TV', search: 'Search', myspace: 'My Space' },
    ar: { home: 'الرئيسية', livetv: 'البث المباشر', search: 'البحث', myspace: 'مساحتي' },
};

const routerLink = (href: string, guestGated = false) => ({
    href,
    linkComponent: Link,
    // mirrors the header menu's guestNavProps
    transformLinkProps: ({ href }: { href: string }) =>
        guestGated ? { to: href, onClick: (e: MouseEvent) => showGuestPromotionPopup(e) } : { to: href },
});

export const useMobileTabs = (): BottomTabItem[] => {
    const dispatch = useDispatch();
    const language = useLanguage();
    const { pathname } = useLocation();
    const { selectedProfile } = useSelector((state: IStore) => state.profile);
    const searchOpen = useSelector((state: IStore) => state.header?.searchOverlay);
    const { data: avatarData } = useProfileAvatars();
    const { data: headerMenu } = useHeaderMenuQuery();
    const guestOpenScreens = useMemo(() => getGuestOpenScreensFromMenu(headerMenu), [headerMenu]);
    const isGuest = isGuestUser(getUserInfo());
    const guestGated = (segment: string) => isGuest && !isGuestAllowedPrimarySegment(segment, guestOpenScreens);
    const avatarSrc = getProfileAvatar(selectedProfile, avatarData);
    const labels = LABELS[language === 'ar' ? 'ar' : 'en'];
    const section = getPathNameWithoutLangPrefix(pathname).split('/')[0];

    const onHome = !searchOpen && (section === '' || section === 'kids-home');
    const onLiveTv = !searchOpen && section === 'livetv';

    return [
        {
            label: labels.home,
            icon: onHome ? 'home-filled' : 'home',
            isActive: onHome,
            ...routerLink(url.route(selectedProfile?.kidsMode ? 'kids-home' : '')),
        },
        {
            label: labels.livetv,
            icon: onLiveTv ? 'live-filled' : 'live',
            isActive: onLiveTv,
            ...routerLink(url.route('livetv'), guestGated('livetv')),
        },
        {
            label: labels.search,
            icon: searchOpen ? 'search-filled' : 'search',
            isActive: !!searchOpen,
            href: '#',
            transformLinkProps: ({ href }) => ({
                href,
                role: 'button',
                onClick: (e: MouseEvent) => {
                    e.preventDefault();
                    dispatch(openSearchInputOverlay(true));
                },
            }),
        },
        {
            label: labels.myspace,
            isActive: !searchOpen && section === 'watchlist',
            ...(avatarSrc ? { media: <Avatar image={avatarSrc} width="4x" /> } : { icon: 'account' as const }),
            ...routerLink(url.route('watchlist'), guestGated('watchlist')),
        },
    ];
};
